/**
 * PlazaCategoriesManager — department visibility + ordering for the
 * Plaza shop rail. Red/green show-hide pill per category, select-all +
 * bulk display toggles, and hamburger drag-&-drop ordering (ajax save).
 * Consistent with the vendor-shop AdminCategories enable/disable UX.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { GripVertical, ChevronDown, Loader2, Eye, EyeOff, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API, withCredentials: true });

export default function PlazaCategoriesManager() {
  const [cats, setCats] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");
  const [sel, setSel] = useState(new Set());
  const [openId, setOpenId] = useState(null);
  const dragId = useRef(null);

  const load = async () => {
    setErr("");
    try {
      const r = await api.get("/api/dropshipzone/admin/categories");
      setCats(r.data);
    } catch (_) { setErr("Couldn't load categories — are you signed in as an operator?"); }
  };
  useEffect(() => { load(); }, []);

  // Departments = children of taxonomy roots (same logic as the shop rail).
  const { tops, childrenOf } = useMemo(() => {
    const list = cats || [];
    const ids = new Set(list.map((c) => c.category_id));
    const roots = list.filter((c) => !c.parent_id || !ids.has(c.parent_id));
    const rootIds = new Set(roots.map((c) => c.category_id));
    let tops = list.filter((c) => rootIds.has(c.parent_id));
    if (!tops.length) tops = roots;
    const childrenOf = {};
    list.forEach((c) => {
      if (!c.parent_id) return;
      (childrenOf[c.parent_id] = childrenOf[c.parent_id] || []).push(c);
    });
    return { tops, childrenOf };
  }, [cats]);

  const note = (m) => { setFlash(m); setTimeout(() => setFlash(""), 4000); };

  const setVisibility = async (ids, visible) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      await api.put("/api/dropshipzone/admin/categories/visibility",
        { category_ids: ids, visible });
      setCats((cs) => cs.map((c) =>
        ids.includes(c.category_id) ? { ...c, hp_visible: visible } : c));
      note(`${ids.length} categor${ids.length === 1 ? "y" : "ies"} set to ${visible ? "SHOW" : "HIDE"}.`);
    } catch (_) { note("Couldn't save visibility."); }
    setBusy(false);
  };

  const toggleSel = (id) => setSel((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allSelected = tops.length > 0 && tops.every((c) => sel.has(c.category_id));
  const selectAll = () =>
    setSel(allSelected ? new Set() : new Set(tops.map((c) => c.category_id)));

  const onDrop = async (targetId) => {
    const src = dragId.current;
    dragId.current = null;
    if (!src || src === targetId) return;
    const order = tops.map((c) => c.category_id);
    const from = order.indexOf(src);
    const to = order.indexOf(targetId);
    if (from < 0 || to < 0) return;
    order.splice(to, 0, order.splice(from, 1)[0]);
    // Optimistic reorder
    setCats((cs) => {
      const pos = {}; order.forEach((id, i) => { pos[id] = (i + 1) * 10; });
      return cs.map((c) => pos[c.category_id]
        ? { ...c, hp_sort: pos[c.category_id] } : c);
    });
    try {
      await api.put("/api/dropshipzone/admin/categories/order", { category_ids: order });
      note("Order saved.");
    } catch (_) { note("Couldn't save the new order."); load(); }
  };

  const sortedTops = useMemo(() =>
    [...tops].sort((a, b) =>
      (a.hp_sort ?? 1e9) - (b.hp_sort ?? 1e9) ||
      (a.name || "").localeCompare(b.name || "")), [tops]);

  if (err) return <p style={{ color: "#FF9F87", fontSize: 13 }}>{err}</p>;
  if (!cats) return <p style={{ fontSize: 13, opacity: 0.7 }}><Loader2 size={14} className="psa-spin" style={{ display: "inline" }} /> Loading categories…</p>;

  const pill = (on) => ({
    fontSize: 11, fontWeight: 700, padding: "3px 12px", borderRadius: 999,
    cursor: "pointer", border: "1px solid",
    background: on ? "#123B24" : "#3B1512",
    borderColor: on ? "#2D7A4F" : "#B7362A",
    color: on ? "#63D08D" : "#FF9F87",
  });

  return (
    <div data-testid="plaza-categories-manager">
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
          <input type="checkbox" checked={allSelected} onChange={selectAll}
            data-testid="plaza-cats-select-all" />
          Select all ({sel.size})
        </label>
        <button disabled={busy || !sel.size} onClick={() => setVisibility([...sel], true)}
          data-testid="plaza-cats-bulk-show"
          style={{ ...pill(true), opacity: sel.size ? 1 : 0.4, display: "inline-flex", alignItems: "center", gap: 5 }}>
          <Eye size={12} /> Show selected
        </button>
        <button disabled={busy || !sel.size} onClick={() => setVisibility([...sel], false)}
          data-testid="plaza-cats-bulk-hide"
          style={{ ...pill(false), opacity: sel.size ? 1 : 0.4, display: "inline-flex", alignItems: "center", gap: 5 }}>
          <EyeOff size={12} /> Hide selected
        </button>
        <button onClick={load} disabled={busy} title="Reload"
          data-testid="plaza-cats-reload"
          style={{ background: "none", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 999,
            color: "inherit", padding: "3px 10px", fontSize: 11, cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 5 }}>
          <RefreshCw size={11} /> Reload
        </button>
        {flash && <span style={{ fontSize: 12, color: "#63D08D" }} data-testid="plaza-cats-flash">{flash}</span>}
      </div>
      <p style={{ fontSize: 11.5, opacity: 0.6, margin: "0 0 10px" }}>
        Drag the ≡ handle to reorder departments · red/green pill shows or hides it on the shop.
        Hidden departments (and their products' category filter) disappear from the storefront rail instantly.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {sortedTops.map((c) => {
          const kids = (childrenOf[c.category_id] || []).sort(
            (a, b) => (a.name || "").localeCompare(b.name || ""));
          const open = openId === c.category_id;
          return (
            <div key={c.category_id}>
              <div
                draggable
                onDragStart={() => { dragId.current = c.category_id; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(c.category_id)}
                data-testid={`plaza-cat-row-${c.category_id}`}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 10px",
                  borderRadius: 8, background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  opacity: c.hp_visible ? 1 : 0.55 }}>
                <GripVertical size={15} style={{ cursor: "grab", opacity: 0.5, flexShrink: 0 }}
                  data-testid={`plaza-cat-drag-${c.category_id}`} />
                <input type="checkbox" checked={sel.has(c.category_id)}
                  onChange={() => toggleSel(c.category_id)}
                  data-testid={`plaza-cat-check-${c.category_id}`} />
                <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600 }}>
                  {c.name}
                  <span style={{ fontSize: 11, opacity: 0.55, fontWeight: 400, marginLeft: 8 }}>
                    {c.live_products} live · {kids.length} subcats
                  </span>
                </span>
                {kids.length > 0 && (
                  <button onClick={() => setOpenId(open ? null : c.category_id)}
                    data-testid={`plaza-cat-expand-${c.category_id}`}
                    style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", padding: 2 }}>
                    <ChevronDown size={14} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
                  </button>
                )}
                <button disabled={busy}
                  onClick={() => setVisibility([c.category_id], !c.hp_visible)}
                  data-testid={`plaza-cat-toggle-${c.category_id}`}
                  style={pill(c.hp_visible)}>
                  {c.hp_visible ? "SHOWN" : "HIDDEN"}
                </button>
              </div>
              {open && kids.length > 0 && (
                <div style={{ margin: "2px 0 6px 44px", display: "flex", flexDirection: "column", gap: 2 }}
                  data-testid={`plaza-cat-subs-${c.category_id}`}>
                  {kids.map((k) => (
                    <div key={k.category_id}
                      style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 10px",
                        borderRadius: 6, fontSize: 12.5, background: "rgba(255,255,255,0.02)",
                        opacity: k.hp_visible ? 1 : 0.55 }}>
                      <span style={{ flex: 1 }}>{k.name}
                        <span style={{ fontSize: 10.5, opacity: 0.5, marginLeft: 6 }}>{k.live_products} live</span>
                      </span>
                      <button disabled={busy}
                        onClick={() => setVisibility([k.category_id], !k.hp_visible)}
                        data-testid={`plaza-cat-toggle-${k.category_id}`}
                        style={{ ...pill(k.hp_visible), fontSize: 10, padding: "2px 9px" }}>
                        {k.hp_visible ? "SHOWN" : "HIDDEN"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
